/**
 * Saga Transaction Infrastructure
 *
 * Implements the Saga pattern for managing distributed transactions with compensation logic.
 * This ensures that either all steps in a transaction succeed or are properly compensated.
 */

export interface SagaStep<T = any> {
  id: string
  execute: () => Promise<T>
  compensate: () => Promise<void>
  description: string
}

export interface SagaExecutionContext {
  stepId: string
  stepDescription: string
  startTime: number
  endTime?: number
  result?: any
  error?: Error
  compensated?: boolean
}

export interface SagaResult<T = any> {
  success: boolean
  result?: T
  error?: Error
  executedSteps: SagaExecutionContext[]
  compensatedSteps: string[]
  totalDuration: number
}

export class SagaTransaction<T = any> {
  private steps: SagaStep<any>[] = []
  private executedSteps: SagaExecutionContext[] = []
  private readonly startTime: number

  private static defaultLogger(): void {
    if (process.env.NODE_ENV === 'development') {
      // Debug logging removed
    }
  }

  constructor(
    private readonly transactionId: string = `saga_${Date.now()}_${Math.random()
      .toString(36)
      .substr(2, 9)}`,
    private readonly logger: (
      level: 'info' | 'warn' | 'error',
      message: string,
      data?: any
    ) => void = SagaTransaction.defaultLogger
  ) {
    this.startTime = Date.now()
    this.logger('info', `Saga transaction started: ${this.transactionId}`)
  }

  /**
   * Add a step to the saga transaction
   */
  addStep<R>(step: SagaStep<R>): SagaTransaction<T> {
    this.steps.push(step)
    this.logger('info', `Step added to saga: ${step.id} - ${step.description}`)
    return this
  }

  /**
   * Execute all steps in the saga
   */
  async execute(): Promise<SagaResult<T>> {
    const compensatedSteps: string[] = []
    let lastResult: any = null

    try {
      // Execute all steps sequentially
      for (const step of this.steps) {
        const context: SagaExecutionContext = {
          stepId: step.id,
          stepDescription: step.description,
          startTime: Date.now()
        }

        this.logger('info', `Executing step: ${step.id}`, {
          step: step.description
        })

        try {
          const result = await step.execute()
          context.endTime = Date.now()
          context.result = result
          lastResult = result

          this.executedSteps.push(context)
          this.logger('info', `Step completed: ${step.id}`, {
            duration: context.endTime - context.startTime,
            result: result
          })
        } catch (error) {
          context.endTime = Date.now()
          context.error = error as Error
          this.executedSteps.push(context)

          this.logger('error', `Step failed: ${step.id}`, {
            error: error instanceof Error ? error.message : error,
            duration: context.endTime - context.startTime
          })

          throw error
        }
      }

      const totalDuration = Date.now() - this.startTime
      this.logger(
        'info',
        `Saga transaction completed successfully: ${this.transactionId}`,
        {
          totalDuration,
          stepsExecuted: this.executedSteps.length
        }
      )

      return {
        success: true,
        result: lastResult as T,
        executedSteps: this.executedSteps,
        compensatedSteps,
        totalDuration
      }
    } catch (error) {
      this.logger('error', `Saga transaction failed: ${this.transactionId}`, {
        error: error instanceof Error ? error.message : error
      })

      // Compensate all executed steps in reverse order
      const compensationResults = await this.compensate()
      compensatedSteps.push(...compensationResults)

      const totalDuration = Date.now() - this.startTime
      return {
        success: false,
        error: error as Error,
        executedSteps: this.executedSteps,
        compensatedSteps,
        totalDuration
      }
    }
  }

  /**
   * Compensate all executed steps in reverse order
   */
  private async compensate(): Promise<string[]> {
    const compensatedSteps: string[] = []

    // Compensate in reverse order of execution
    const stepsToCompensate = [...this.executedSteps]
      .reverse()
      .filter((context) => !context.error && !context.compensated)

    this.logger(
      'info',
      `Starting compensation for ${stepsToCompensate.length} steps`
    )

    for (const context of stepsToCompensate) {
      const step = this.steps.find((s) => s.id === context.stepId)
      if (!step) {
        this.logger(
          'warn',
          `Cannot find step for compensation: ${context.stepId}`
        )
        continue
      }

      try {
        this.logger('info', `Compensating step: ${step.id}`)
        await step.compensate()
        context.compensated = true
        compensatedSteps.push(step.id)
        this.logger('info', `Step compensated: ${step.id}`)
      } catch (compensationError) {
        this.logger('error', `Failed to compensate step: ${step.id}`, {
          error:
            compensationError instanceof Error
              ? compensationError.message
              : compensationError
        })
        // Continue with other compensations even if one fails
      }
    }

    this.logger(
      'info',
      `Compensation completed. Compensated ${compensatedSteps.length} steps`,
      {
        compensatedSteps
      }
    )

    return compensatedSteps
  }

  /**
   * Get the current state of the transaction
   */
  getState(): {
    transactionId: string
    totalSteps: number
    executedSteps: number
    isRunning: boolean
  } {
    return {
      transactionId: this.transactionId,
      totalSteps: this.steps.length,
      executedSteps: this.executedSteps.length,
      isRunning:
        this.executedSteps.length > 0 &&
        this.executedSteps.length < this.steps.length
    }
  }
}

/**
 * Utility function to create a saga step
 */
export function createSagaStep<T>(
  id: string,
  description: string,
  execute: () => Promise<T>,
  compensate: () => Promise<void>
): SagaStep<T> {
  return {
    id,
    description,
    execute,
    compensate
  }
}

/**
 * Delay utility for saga steps
 */
export function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}
